"use client";
import { useEffect, type ReactNode } from "react";
import { usePathname } from "next/navigation";
import { LANG_STORAGE_KEY, type Lang } from "@/components/lms/hardcoded/i18n";
import { useAuth, LS_USER } from "@/hooks/use-auth";

function normalizeLang(v: string | null): Lang {
  return v === "ar" || v === "fr" ? v : "en";
}

// (dashboard) is a route group and is stripped from the URL, so the live
// pathname for client/app/(dashboard)/session/[id]/page.tsx is /session/<id>.
const SESSION_PLAYER_ROUTE_RE = /^\/session\/[^/]+$/;

function applyDocumentDir(lang: Lang, rtlEligible: boolean) {
  const html = document.documentElement;
  html.lang = lang;
  html.dir = rtlEligible && lang === "ar" ? "rtl" : "ltr";
}

// Runs before hydration (no router/auth state yet). Mirrors the client gate:
// cached-role student check for most routes, but the session player forces
// rtlEligible from the URL alone since teacher/admin can land there directly
// and the content is identical regardless of role.
const INIT_SCRIPT = `(function(){try{var l=localStorage.getItem(${JSON.stringify(
  LANG_STORAGE_KEY,
)});var lang=l==="ar"||l==="fr"?l:"en";document.documentElement.lang=lang;var raw=localStorage.getItem(${JSON.stringify(
  LS_USER,
)});var u=raw?JSON.parse(raw):null;var isStudent=!!u&&u.role==="student";var isSessionRoute=/^\\/session\\/[^/]+$/.test(location.pathname);document.documentElement.dir=((isStudent||isSessionRoute)&&lang==="ar")?"rtl":"ltr";}catch(e){}})();`;

/**
 * Sets `dir`/`lang` on `<html>` from the learner's `lms-lang` choice (H-02).
 * RTL applies when `role === "student"` anywhere, OR on the session player
 * route (`/session/[id]`) for any authenticated role, since teacher/admin
 * preview the same Arabic session content students see there. Every other
 * route for non-student roles stays LTR.
 */
export function LanguageProvider({ children }: { children: ReactNode }) {
  const { user, isLoading } = useAuth();
  const pathname = usePathname();
  const isStudent = !isLoading && user?.role === "student";
  const isSessionRoute = SESSION_PLAYER_ROUTE_RE.test(pathname ?? "");
  const rtlEligible = isStudent || isSessionRoute;

  useEffect(() => {
    const sync = () => applyDocumentDir(normalizeLang(localStorage.getItem(LANG_STORAGE_KEY)), rtlEligible);
    sync();
    window.addEventListener("lms-lang-changed", sync);
    window.addEventListener("storage", sync);
    return () => {
      window.removeEventListener("lms-lang-changed", sync);
      window.removeEventListener("storage", sync);
    };
  }, [rtlEligible]);

  return (
    <>
      <script suppressHydrationWarning dangerouslySetInnerHTML={{ __html: INIT_SCRIPT }} />
      {children}
    </>
  );
}
